import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Badge, InputGroup, Dropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Footer from '../components/Footer';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [convocatorias, setConvocatorias] = useState([]);
  const [convocatoriasOriginales, setConvocatoriasOriginales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('TODOS');
  const [rubroFiltro, setRubroFiltro] = useState('TODOS');

  useEffect(() => {
    loadConvocatoriasPublicas();
  }, []);

  const loadConvocatoriasPublicas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/convocatorias/public');
      // Asegurar que siempre sea un array
      const data = Array.isArray(response.data) ? response.data : [];
      setConvocatoriasOriginales(data);
      setConvocatorias(data);
    } catch (error) {
      console.error('Error loading convocatorias:', error);
      setConvocatoriasOriginales([]);
      setConvocatorias([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const role = Number(user?.rol ?? localStorage.getItem('userRole'));

  /** Normaliza estado desde API/BD (espacios, mayúsculas) */
  const normalizeEstado = (estado) => String(estado ?? '').trim().toUpperCase();

  const getRubrosUnicos = () => {
    // Asegurar que convocatoriasOriginales sea un array
    if (!Array.isArray(convocatoriasOriginales)) {
      return [];
    }
    const rubros = convocatoriasOriginales.map(conv => conv.rubro).filter(Boolean);
    return [...new Set(rubros)];
  };

  const filtrarConvocatorias = () => {
    // Asegurar que convocatoriasOriginales sea un array
    if (!Array.isArray(convocatoriasOriginales)) {
      return [];
    }
    let filtradas = convocatoriasOriginales;

    // TODOS = todas las del API excepto FINALIZADA (las finalizadas solo con filtro "Finalizadas")
    // ACTIVAS = solo ABIERTA y EVALUACION
    // FINALIZADA = solo al elegir explícitamente ese filtro
    if (estadoFiltro === 'ACTIVAS') {
      filtradas = filtradas.filter((conv) => {
        const e = normalizeEstado(conv.estado);
        return e === 'ABIERTA' || e === 'EVALUACION';
      });
    } else if (estadoFiltro !== 'TODOS') {
      filtradas = filtradas.filter((conv) => normalizeEstado(conv.estado) === estadoFiltro);
    }

    if (estadoFiltro !== 'FINALIZADA') {
      filtradas = filtradas.filter((conv) => normalizeEstado(conv.estado) !== 'FINALIZADA');
    }

    // Filtro de búsqueda por texto
    if (searchTerm) {
      filtradas = filtradas.filter(conv => 
        (conv.COD_CONVOCATORIA && conv.COD_CONVOCATORIA.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conv.concepto && conv.concepto.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conv.rubro && conv.rubro.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conv.motivo_pedido && conv.motivo_pedido.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (conv.nro_convocatoria && conv.nro_convocatoria.toString().includes(searchTerm)) ||
        (conv.nro_pedido && conv.nro_pedido.toString().includes(searchTerm))
      );
    }

    // Filtro por rubro
    if (rubroFiltro !== 'TODOS') {
      filtradas = filtradas.filter(conv => conv.rubro === rubroFiltro);
    }

    return filtradas;
  };

  useEffect(() => {
    setConvocatorias(filtrarConvocatorias());
  }, [searchTerm, estadoFiltro, rubroFiltro, convocatoriasOriginales]);

  const getEstadoVariant = (estado) => {
    switch (normalizeEstado(estado)) {
      case 'ABIERTA':
        return 'success';
      case 'CERRADA':
        return 'secondary';
      case 'EVALUACION':
        return 'warning';
      case 'FINALIZADA':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)), url(/fondo.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem 0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center">
                {!(isAuthenticated && role === 2) && (
                  <img 
                    src="/LOGO-DIRESA.png" 
                    alt="Logo DIRESA" 
                    className="me-3"
                    style={{ 
                      height: '90px', 
                      objectFit: 'contain',
                      filter: 'brightness(0) invert(1)'
                    }}
                  />
                )}
                <div>
                  <h1 className="text-white mb-1 fw-bold" style={{ fontSize: '1.75rem' }}>
                    Sistema de Cotizador
                  </h1>
                  <p className="text-white-50 mb-0 small">DIRESA - Portal Público</p>
                </div>
              </div>
            </Col>
            <Col md={6} className="text-end">
              {!isAuthenticated ? (
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-light"
                    onClick={() => navigate('/registro')}
                    className="px-4"
                  >
                    <i className="bi bi-person-plus me-2"></i>
                    Registrarse
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => navigate('/login')}
                    className="px-4"
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Iniciar Sesión
                  </Button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-3 justify-content-end">
                  {role !== 2 && (
                  <Dropdown align="end">
                    <Dropdown.Toggle 
                    variant="outline-light"
                      id="user-dropdown-home"
                      className="d-flex align-items-center border-0"
                      style={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.75)',
                        color: '#2b3e53',
                        borderRadius: '10px',
                        padding: '0.6rem 1rem',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid rgba(255,255,255,0.9)',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(43, 62, 83, 0.25) 0%, rgba(43, 62, 83, 0.2) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '10px',
                        border: '1px solid rgba(43, 62, 83, 0.3)',
                        transition: 'all 0.3s'
                      }}>
                        <i className="bi bi-person-fill" style={{ fontSize: '1.1rem', color: '#2b3e53' }}></i>
                      </div>
                      <span className="d-none d-md-inline fw-medium" style={{ fontSize: '0.9rem', color: '#2b3e53' }}>
                        {user?.nombre_completo || user?.razon_social || user?.email || 'Usuario'}
                      </span>
                      <i className="bi bi-chevron-down ms-2" style={{ fontSize: '0.75rem', color: '#2b3e53' }}></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu 
                      style={{ 
                        minWidth: '260px', 
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '12px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                        padding: 0,
                        overflow: 'hidden',
                        marginTop: '0.5rem'
                      }}
                    >
                      {/* Header del menú */}
                      <div style={{
                        padding: '1.25rem 1.25rem 1rem 1.25rem',
                        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)',
                        color: '#2b3e53',
                        position: 'relative',
                        overflow: 'hidden',
                        margin: 0,
                        borderBottom: '2px solid #81c784'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: '-50%',
                          right: '-50%',
                          width: '200%',
                          height: '200%',
                          background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)',
                          pointerEvents: 'none'
                        }}></div>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div className="fw-semibold" style={{ fontSize: '1rem', marginBottom: '0.25rem', color: '#2b3e53' }}>
                            {user?.nombre_completo || user?.razon_social || user?.email || 'Usuario'}
                          </div>
                          <div style={{ 
                            opacity: 0.85, 
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#2b3e53'
                          }}>
                            <i className="bi bi-envelope" style={{ fontSize: '0.75rem', color: '#4caf50' }}></i>
                            {user?.email}
                          </div>
                        </div>
                      </div>
                      
                      <Dropdown.Item 
                        as={Link} 
                        to="/perfil"
                        style={{ 
                          padding: '0.875rem 1.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f8fafc';
                          e.target.style.paddingLeft = '1.5rem';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.paddingLeft = '1.25rem';
                        }}
                      >
                        <i className="bi bi-person me-2" style={{ color: '#667eea', fontSize: '1.1rem', width: '24px' }}></i>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Información de Usuario</span>
                      </Dropdown.Item>
                      
                      {role === 2 && (
                        <Dropdown.Item 
                          as={Link} 
                          to="/mis-cotizaciones" 
                          style={{ 
                            padding: '0.875rem 1.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8fafc';
                            e.target.style.paddingLeft = '1.5rem';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.paddingLeft = '1.25rem';
                          }}
                        >
                          <i className="bi bi-file-earmark-text me-2" style={{ color: '#667eea', fontSize: '1.1rem', width: '24px' }}></i>
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Mis Postulaciones</span>
                        </Dropdown.Item>
                      )}
                      
                      {role === 2 && (
                        <Dropdown.Item
                          as={Link}
                          to="/mis-ordenes"
                          style={{
                            padding: '0.875rem 1.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8fafc';
                            e.target.style.paddingLeft = '1.5rem';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.paddingLeft = '1.25rem';
                          }}
                        >
                          <i
                            className="bi bi-file-earmark-richtext me-2"
                            style={{ color: '#667eea', fontSize: '1.1rem', width: '24px' }}
                          />
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Mis órdenes</span>
                        </Dropdown.Item>
                      )}
                      
                      {role !== 2 && (
                        <Dropdown.Item 
                          as={Link} 
                          to="/dashboard" 
                          style={{ 
                            padding: '0.875rem 1.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8fafc';
                            e.target.style.paddingLeft = '1.5rem';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.paddingLeft = '1.25rem';
                          }}
                        >
                          <i className="bi bi-speedometer2 me-2" style={{ color: '#667eea', fontSize: '1.1rem', width: '24px' }}></i>
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Dashboard</span>
                        </Dropdown.Item>
                      )}
                      
                      <Dropdown.Divider style={{ margin: '0.25rem 0', borderColor: '#e5e7eb' }} />
                      
                      <Dropdown.Item 
                        as={Link} 
                        to="/change-password" 
                        style={{ 
                          padding: '0.875rem 1.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f8fafc';
                          e.target.style.paddingLeft = '1.5rem';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.paddingLeft = '1.25rem';
                        }}
                      >
                        <i className="bi bi-key me-2" style={{ color: '#667eea', fontSize: '1.1rem', width: '24px' }}></i>
                        <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Cambiar Contraseña</span>
                      </Dropdown.Item>
                      
                      <Dropdown.Divider style={{ margin: '0.25rem 0', borderColor: '#e5e7eb' }} />
                      
                      <Dropdown.Item 
                    onClick={handleLogout}
                        style={{ 
                          padding: '0.875rem 1.25rem',
                          color: '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fee2e2';
                          e.target.style.paddingLeft = '1.5rem';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.paddingLeft = '1.25rem';
                        }}
                      >
                        <i className="bi bi-box-arrow-right me-2" style={{ fontSize: '1.1rem', width: '24px' }}></i>
                        <span style={{ fontSize: '0.9rem' }}>Cerrar Sesión</span>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Contenido Principal */}
      <Container className="py-5">
        <Row>
          {/* Columna Izquierda - Filtros y Búsqueda */}
          <Col lg={4} className="mb-4 mb-lg-0">
            <div style={{ position: 'sticky', top: '20px' }}>
              <Card className="border-0 shadow-sm mb-3" style={{ backgroundColor: '#f0f7f4' }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3" style={{ color: '#2b3e53', fontSize: '1.1rem' }}>
                    <i className="bi bi-funnel me-2"></i>
                    Buscar y Filtrar
                  </h5>
                  
        <div className="mb-4">
                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-search me-2"></i>
                  Buscar
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por código, concepto, rubro, motivo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ fontSize: '0.875rem' }}
                  />
                      <InputGroup.Text style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                </InputGroup>
                  </div>

                  <div className="mb-4">
                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-funnel me-2"></i>
                  Estado
                </Form.Label>
                <Form.Select
                  value={estadoFiltro}
                  onChange={(e) => setEstadoFiltro(e.target.value)}
                      style={{ fontSize: '0.875rem' }}
                >
                  <option value="TODOS">Todos (sin finalizadas)</option>
                  <option value="ACTIVAS">Activas (Abiertas / Evaluación)</option>
                  <option value="ABIERTA">Solo abiertas</option>
                  <option value="EVALUACION">En evaluación</option>
                  <option value="CERRADA">Cerradas</option>
                  <option value="FINALIZADA">Finalizadas</option>
                </Form.Select>
                <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Las convocatorias finalizadas solo aparecen al elegir &quot;Finalizadas&quot;.
                </Form.Text>
                  </div>

                  <div className="mb-4">
                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                  <i className="bi bi-tag me-2"></i>
                  Rubro
                </Form.Label>
                <Form.Select
                  value={rubroFiltro}
                  onChange={(e) => setRubroFiltro(e.target.value)}
                      style={{ fontSize: '0.875rem' }}
                >
                  <option value="TODOS">Todos los rubros</option>
                  {getRubrosUnicos().map(rubro => (
                    <option key={rubro} value={rubro}>{rubro}</option>
                  ))}
                </Form.Select>
                  </div>

              {(searchTerm || estadoFiltro !== 'TODOS' || rubroFiltro !== 'TODOS') && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setEstadoFiltro('TODOS');
                      setRubroFiltro('TODOS');
                    }}
                      className="w-100 mb-3"
                      style={{ fontSize: '0.875rem' }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                      Limpiar Filtros
                  </Button>
                  )}

                  <div className="border-top pt-3">
                    <h6 className="fw-semibold mb-2" style={{ fontSize: '0.875rem', color: '#2b3e53' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      Estados disponibles:
                    </h6>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center">
                        <Badge bg="success" className="me-2" style={{ width: '10px', height: '10px', padding: 0 }}></Badge>
                        <small className="text-muted" style={{ fontSize: '0.875rem' }}>ABIERTA - Puedes participar</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge bg="warning" className="me-2" style={{ width: '10px', height: '10px', padding: 0 }}></Badge>
                        <small className="text-muted" style={{ fontSize: '0.875rem' }}>EVALUACIÓN - En proceso</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge bg="secondary" className="me-2" style={{ width: '10px', height: '10px', padding: 0 }}></Badge>
                        <small className="text-muted" style={{ fontSize: '0.875rem' }}>CERRADA - Plazo de postulación cerrado</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge bg="info" className="me-2" style={{ width: '10px', height: '10px', padding: 0 }}></Badge>
                        <small className="text-muted" style={{ fontSize: '0.875rem' }}>FINALIZADA - Proceso cerrado</small>
                      </div>
                    </div>
                  </div>
          </Card.Body>
        </Card>
            </div>
          </Col>

          {/* Columna Derecha - Convocatorias Disponibles */}
          <Col lg={8}>
            <div className="mb-4">
              <h2 className="fw-bold mb-2" style={{ color: '#2b3e53' }}>
                <i className="bi bi-file-earmark-text me-2"></i>
                Convocatorias Disponibles
              </h2>
              <p className="text-muted">Explora las convocatorias activas y participa en los procesos</p>
            </div>

        {/* Resultados */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-3 text-muted">Cargando convocatorias...</p>
          </div>
        ) : convocatorias.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#adb5bd' }}></i>
              <p className="mt-3 text-muted mb-0">
                {searchTerm || estadoFiltro !== 'TODOS' || rubroFiltro !== 'TODOS'
                  ? 'No se encontraron convocatorias con los filtros seleccionados.'
                  : 'No hay convocatorias disponibles en este momento.'}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <>
            <div className="mb-3">
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                <i className="bi bi-info-circle me-2"></i>
                Mostrando <strong>{convocatorias.length}</strong> convocatoria{convocatorias.length !== 1 ? 's' : ''}
              </p>
            </div>
            <Row className="g-4">
              {Array.isArray(convocatorias) && convocatorias.map((conv) => (
                    <Col key={conv.nro_convocatoria} xs={12}>
                      <Card className="h-100 border-0 shadow-sm d-flex flex-column" 
                    style={{ 
                          borderRadius: '12px',
                      transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(240, 247, 244, 0.8)'
                    }}
                    onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 8px 20px rgba(43, 62, 83, 0.15)';
                          e.currentTarget.style.borderColor = 'rgba(43, 62, 83, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = 'rgba(240, 247, 244, 0.8)';
                        }}
                      >
                        <Card.Body className="p-4 d-flex flex-column flex-grow-1" style={{ backgroundColor: '#fafbfc' }}>
                          {/* Header - Código, Tipo, Año y Estado */}
                          <div className="mb-3 pb-3" style={{ borderBottom: '2px solid rgba(240, 247, 244, 0.6)' }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="d-flex align-items-center flex-grow-1">
                            <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '12px',
                                  background: 'linear-gradient(135deg, rgba(240, 247, 244, 0.8) 0%, rgba(230, 245, 238, 0.9) 100%)',
                                  border: '1px solid rgba(200, 230, 215, 0.5)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px'
                            }}>
                                  <i className="bi bi-file-earmark-text" style={{ fontSize: '1.5rem', color: '#2b3e53' }}></i>
                            </div>
                                <div className="flex-grow-1">
                                  <small className="d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6c757d', letterSpacing: '0.5px' }}>
                                    CODIGO DE CONVOCATORIA
                                  </small>
                                  <h5 className="mb-0 fw-bold" style={{ color: '#2b3e53', fontSize: '1.15rem' }}>
                                {conv.COD_CONVOCATORIA || conv.nro_convocatoria}
                              </h5>
                            </div>
                          </div>
                              <Badge bg={getEstadoVariant(conv.estado)} className="px-3 py-2" style={{ fontSize: '0.875rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                          {normalizeEstado(conv.estado) || conv.estado || '—'}
                        </Badge>
                      </div>
                            {/* Tipo, Número de Pedido y Año en el mismo nivel */}
                            <Row className="g-2 mt-2">
                              <Col xs={6} md={4}>
                                <div className="d-flex align-items-center">
                                  <small style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6c757d', marginRight: '0.5rem' }}>
                                    TIPO:
                                  </small>
                                  <span className="fw-semibold" style={{ color: '#2b3e53', fontSize: '0.875rem' }}>
                            {conv.tipo_bien === 'S' ? 'Servicio' : conv.tipo_bien === 'B' ? 'Bienes' : 'N/A'}
                          </span>
                        </div>
                              </Col>
                              <Col xs={6} md={4}>
                                <div className="d-flex align-items-center">
                                  <small style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6c757d', marginRight: '0.5rem' }}>
                                    NRO. PEDIDO:
                                  </small>
                                  <span className="fw-semibold" style={{ color: '#2b3e53', fontSize: '0.875rem' }}>
                                    {conv.nro_pedido || 'N/A'}
                                  </span>
                        </div>
                              </Col>
                              <Col xs={6} md={4}>
                                <div className="d-flex align-items-center">
                                  <small style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6c757d', marginRight: '0.5rem' }}>
                                    AÑO:
                                  </small>
                                  <span className="fw-semibold" style={{ color: '#2b3e53', fontSize: '0.875rem' }}>
                                    {conv.ano_eje || 'N/A'}
                              </span>
                            </div>
                              </Col>
                            </Row>
                          </div>

                          {/* Información principal */}
                          <div className="mb-3 flex-grow-1">
                            {/* Fechas destacadas */}
                        {(conv.fecha_inicio || conv.fecha_fin) && (
                              <div className="mb-3 p-3 rounded" style={{ 
                                background: 'linear-gradient(135deg, rgba(230, 240, 250, 0.6) 0%, rgba(240, 247, 244, 0.8) 100%)',
                                border: '1px solid rgba(200, 220, 240, 0.4)'
                              }}>
                                <Row className="g-3">
                              {conv.fecha_inicio && (
                                <Col xs={6}>
                                      <div className="d-flex align-items-center mb-1">
                                        <div style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '8px',
                                          backgroundColor: 'rgba(100, 180, 200, 0.15)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          marginRight: '8px'
                                        }}>
                                          <i className="bi bi-calendar-check" style={{ color: '#5a9fb8', fontSize: '0.875rem' }}></i>
                                        </div>
                                        <small style={{ fontSize: '0.7rem', fontWeight: '600', color: '#5a7a8a' }}>
                                          FECHA INICIO DE POSTULACIÓN
                                  </small>
                                      </div>
                                      <div className="fw-bold" style={{ color: '#2b3e53', fontSize: '0.9rem' }}>
                                    {new Date(conv.fecha_inicio).toLocaleDateString('es-PE', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                      </div>
                                </Col>
                              )}
                              {conv.fecha_fin && (
                                <Col xs={6}>
                                      <div className="d-flex align-items-center mb-1">
                                        <div style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '8px',
                                          backgroundColor: 'rgba(200, 150, 180, 0.15)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          marginRight: '8px'
                                        }}>
                                          <i className="bi bi-calendar-x" style={{ color: '#c896b4', fontSize: '0.875rem' }}></i>
                                        </div>
                                        <small style={{ fontSize: '0.7rem', fontWeight: '600', color: '#5a7a8a' }}>
                                          FECHA FIN DE POSTULACIÓN
                                  </small>
                                      </div>
                                      <div className="fw-bold" style={{ color: '#2b3e53', fontSize: '0.9rem' }}>
                                    {new Date(conv.fecha_fin).toLocaleDateString('es-PE', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                      </div>
                                </Col>
                              )}
                            </Row>
                          </div>
                        )}

                            {/* Motivo del pedido */}
                            {conv.motivo_pedido && (
                              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(240, 247, 244, 0.6)' }}>
                                <div className="d-flex align-items-center mb-2">
                                  <div style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(150, 200, 180, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '8px'
                                  }}>
                                    <i className="bi bi-file-text" style={{ color: '#96c8b4', fontSize: '0.75rem' }}></i>
                                  </div>
                                  <small style={{ fontSize: '0.7rem', fontWeight: '700', color: '#6c757d' }}>
                                    <strong>MOTIVO DEL PEDIDO</strong>
                                  </small>
                                </div>
                                <p className="mb-0 p-2 rounded" style={{ 
                                  color: '#495057', 
                                  fontSize: '0.875rem', 
                                  lineHeight: '1.5',
                                  backgroundColor: 'rgba(250, 250, 250, 0.8)',
                                  border: '1px solid rgba(240, 240, 240, 0.5)'
                                }}>
                                  {conv.motivo_pedido.length > 120 
                                    ? `${conv.motivo_pedido.substring(0, 120)}...` 
                                    : conv.motivo_pedido}
                                </p>
                          </div>
                        )}
                      </div>

                      {/* Botón Ver Detalles */}
                      <Button
                        variant="primary"
                            className="w-100 mt-auto"
                            style={{ 
                              background: 'linear-gradient(135deg, #2b3e53 0%, #3a4f63 100%)',
                              border: 'none',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              padding: '0.75rem',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(43, 62, 83, 0.2)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #3a4f63 0%, #2b3e53 100%)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(43, 62, 83, 0.3)';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(135deg, #2b3e53 0%, #3a4f63 100%)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(43, 62, 83, 0.2)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        onClick={() => navigate(`/convocatoria-details/${conv.nro_convocatoria}`)}
                      >
                        <i className="bi bi-eye me-2"></i>
                        Ver Detalles
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
}

export default Home;
